/**
 * Returns `path` only if it's a same-origin, internal-only relative path — otherwise `null`.
 *
 * Use this before passing a navigation target that originated from user-controllable input (e.g.
 * a `returnTo` query param baked into a shared/bookmarked link) to `navigate()` or `<Link to>`.
 * Those APIs pass an absolute-looking `to` value straight through as the rendered `<a href>`
 * unvalidated, so a crafted link value like `https://evil.com`, a protocol-relative `//evil.com`,
 * or a `javascript:` URL would otherwise redirect the browser off-site (or run script) — most
 * directly via a native `<a href>` middle-click/"open in new tab", which bypasses the app's own
 * click handling entirely.
 *
 * Returns the original `path` rather than the parsed URL's pathname: parsing collapses dot
 * segments, so `/.//evil.com` would come back as `//evil.com`, a protocol-relative URL.
 *
 * @param path - The candidate path, or null if absent (e.g. a missing search param).
 * @returns `path` unchanged if it's a safe internal path, otherwise `null`.
 */
export function sanitizeInternalPath(path: string | null): string | null {
  if (!path || !path.startsWith("/")) {
    return null;
  }
  try {
    if (new URL(path, window.location.origin).origin !== window.location.origin) {
      return null;
    }
  } catch {
    return null;
  }
  return path;
}
